/**
 * Route tree.
 *
 * Three trees share one Vite app:
 *   /        marketing site (public)
 *   /app     consumer application (authenticated)
 *   /admin   admin dashboard (role-restricted)
 *
 * Each is lazily loaded, so a marketing visitor never downloads the editor or
 * the admin bundle. Route guards are for user experience only — the API
 * enforces access independently.
 */

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { MarketingLayout } from './layouts/MarketingLayout.jsx';
import { AppLayout } from './layouts/AppLayout.jsx';
import { AdminLayout } from './layouts/AdminLayout.jsx';
import { AuthLayout } from './layouts/AuthLayout.jsx';

import { ProtectedRoute } from './components/routing/ProtectedRoute.jsx';
import { AdminRoute } from './components/routing/AdminRoute.jsx';
import { PageLoader } from './components/feedback/PageLoader.jsx';
import { ToastViewport } from './components/feedback/ToastViewport.jsx';
import { PaywallDialog } from './components/billing/PaywallDialog.jsx';

/* ------------------------------------------------------------- marketing */
const HomePage = lazy(() => import('./pages/marketing/HomePage.jsx'));
const FeaturesPage = lazy(() => import('./pages/marketing/FeaturesPage.jsx'));
const GuitarPage = lazy(() => import('./pages/marketing/GuitarPage.jsx'));
const PianoPage = lazy(() => import('./pages/marketing/PianoPage.jsx'));
const PricingPage = lazy(() => import('./pages/marketing/PricingPage.jsx'));
const FaqPage = lazy(() => import('./pages/marketing/FaqPage.jsx'));
const ContactPage = lazy(() => import('./pages/marketing/ContactPage.jsx'));
const PrivacyPage = lazy(() => import('./pages/marketing/PrivacyPage.jsx'));
const TermsPage = lazy(() => import('./pages/marketing/TermsPage.jsx'));
const CopyrightPage = lazy(() => import('./pages/marketing/CopyrightPage.jsx'));
const AccountDeletionPage = lazy(() => import('./pages/marketing/AccountDeletionPage.jsx'));
const DownloadPage = lazy(() => import('./pages/marketing/DownloadPage.jsx'));

/* ------------------------------------------------------------------ auth */
const LoginPage = lazy(() => import('./pages/auth/LoginPage.jsx'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage.jsx'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage.jsx'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage.jsx'));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage.jsx'));

/* ------------------------------------------------------------------- app */
const DashboardPage = lazy(() => import('./pages/app/DashboardPage.jsx'));
const SearchPage = lazy(() => import('./pages/app/SearchPage.jsx'));
const LibraryPage = lazy(() => import('./pages/app/LibraryPage.jsx'));
const FavoritesPage = lazy(() => import('./pages/app/FavoritesPage.jsx'));
const RecentPage = lazy(() => import('./pages/app/RecentPage.jsx'));
const DownloadsPage = lazy(() => import('./pages/app/DownloadsPage.jsx'));
const MySongsPage = lazy(() => import('./pages/app/MySongsPage.jsx'));
const SongDetailPage = lazy(() => import('./pages/app/SongDetailPage.jsx'));
const ReaderPage = lazy(() => import('./pages/app/ReaderPage.jsx'));
const EditorPage = lazy(() => import('./pages/app/EditorPage.jsx'));
const PlaylistsPage = lazy(() => import('./pages/app/PlaylistsPage.jsx'));
const PlaylistDetailPage = lazy(() => import('./pages/app/PlaylistDetailPage.jsx'));
const TunerPage = lazy(() => import('./pages/app/TunerPage.jsx'));
const LearnPage = lazy(() => import('./pages/app/LearnPage.jsx'));
const LearnUnitPage = lazy(() => import('./pages/app/LearnUnitPage.jsx'));
const LearnLessonPage = lazy(() => import('./pages/app/LearnLessonPage.jsx'));
const LearnDailyPage = lazy(() => import('./pages/app/LearnDailyPage.jsx'));
const LearnAchievementsPage = lazy(() => import('./pages/app/LearnAchievementsPage.jsx'));
const LearnPracticePage = lazy(() => import('./pages/app/LearnPracticePage.jsx'));
const LearnListeningPage = lazy(() => import('./pages/app/LearnListeningPage.jsx'));
const SettingsPage = lazy(() => import('./pages/app/SettingsPage.jsx'));
const BillingPage = lazy(() => import('./pages/app/BillingPage.jsx'));
const UpgradePage = lazy(() => import('./pages/app/UpgradePage.jsx'));
const OnboardingPage = lazy(() => import('./pages/onboarding/OnboardingPage.jsx'));

/* ----------------------------------------------------------------- admin */
const AdminOverviewPage = lazy(() => import('./pages/admin/AdminOverviewPage.jsx'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage.jsx'));
const AdminUserDetailPage = lazy(() => import('./pages/admin/AdminUserDetailPage.jsx'));
const AdminSubscriptionsPage = lazy(() => import('./pages/admin/AdminSubscriptionsPage.jsx'));
const AdminSongsPage = lazy(() => import('./pages/admin/AdminSongsPage.jsx'));
const AdminModerationPage = lazy(() => import('./pages/admin/AdminModerationPage.jsx'));
const AdminReportsPage = lazy(() => import('./pages/admin/AdminReportsPage.jsx'));
const AdminChordsPage = lazy(() => import('./pages/admin/AdminChordsPage.jsx'));
const AdminLearnPage = lazy(() => import('./pages/admin/AdminLearnPage.jsx'));
const AdminAuditLogPage = lazy(() => import('./pages/admin/AdminAuditLogPage.jsx'));

const NotFoundPage = lazy(() => import('./pages/NotFoundPage.jsx'));

export function App() {
  return (
    <>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Marketing */}
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/guitar" element={<GuitarPage />} />
            <Route path="/piano" element={<PianoPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/copyright" element={<CopyrightPage />} />
            <Route path="/account-deletion" element={<AccountDeletionPage />} />
            <Route path="/download" element={<DownloadPage />} />
          </Route>

          {/* Authentication */}
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
          </Route>

          {/*
            First-run setup. Protected, but deliberately outside AppLayout —
            it is one decision per screen with no nav to wander off into.
          */}
          <Route
            path="/app/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />

          {/* Consumer application */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="search" element={<SearchPage />} />
            <Route path="library" element={<LibraryPage />} />
            <Route path="favorites" element={<FavoritesPage />} />
            <Route path="recent" element={<RecentPage />} />
            <Route path="downloads" element={<DownloadsPage />} />
            <Route path="my-songs" element={<MySongsPage />} />
            <Route path="songs/:songId" element={<SongDetailPage />} />
            <Route path="arrangements/:arrangementId" element={<ReaderPage />} />
            <Route path="editor/new" element={<EditorPage />} />
            <Route path="editor/:arrangementId" element={<EditorPage />} />
            <Route path="playlists" element={<PlaylistsPage />} />
            <Route path="playlists/:playlistId" element={<PlaylistDetailPage />} />
            {/* Learn. `daily`, `practice` and `achievements` come before the
                `:unitId`/`:lessonId` params so they are not read as ids. */}
            <Route path="tuner" element={<TunerPage />} />

            <Route path="learn" element={<LearnPage />} />
            <Route path="learn/daily" element={<LearnDailyPage />} />
            <Route path="learn/practice" element={<LearnPracticePage />} />
            <Route path="learn/listening" element={<LearnListeningPage />} />
            <Route path="learn/achievements" element={<LearnAchievementsPage />} />
            <Route path="learn/unit/:unitId" element={<LearnUnitPage />} />
            <Route path="learn/lesson/:lessonId" element={<LearnLessonPage />} />

            <Route path="settings" element={<SettingsPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="upgrade" element={<UpgradePage />} />
          </Route>

          {/* Admin */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<AdminOverviewPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="users/:userId" element={<AdminUserDetailPage />} />
            <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
            <Route path="songs" element={<AdminSongsPage />} />
            <Route path="moderation" element={<AdminModerationPage />} />
            <Route path="reports" element={<AdminReportsPage />} />
            <Route path="chords" element={<AdminChordsPage />} />
            <Route path="learn" element={<AdminLearnPage />} />
            <Route path="audit-logs" element={<AdminAuditLogPage />} />
          </Route>

          {/* Legacy/convenience redirects */}
          <Route path="/signin" element={<Navigate to="/login" replace />} />
          <Route path="/signup" element={<Navigate to="/register" replace />} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>

      {/* Global overlays live outside the route tree so they survive navigation. */}
      <ToastViewport />
      <PaywallDialog />
    </>
  );
}
